import { useState, useEffect, useRef } from "react";
import { sentinelApi } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Eye, Send, AlertTriangle, TrendingUp, Award,
  Loader2, Sparkles, MessageCircle, RefreshCw
} from "lucide-react";

const INSIGHT_ICONS = { warning: AlertTriangle, opportunity: TrendingUp, praise: Award };
const INSIGHT_COLORS = {
  warning: "border-amber-500/20 bg-amber-500/5 text-amber-400",
  opportunity: "border-cyan-500/20 bg-cyan-500/5 text-cyan-400",
  praise: "border-emerald-500/20 bg-emerald-500/5 text-emerald-400",
};

const SUGGESTED_QUESTIONS = [
  "What should I avoid doing this month?",
  "How can I improve my cashflow position?",
  "What obligations need my attention right now?",
  "Is my business in a healthy financial state?",
  "What's the safest way to use my Free Equity?",
];

export default function SentinelPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const chatEndRef = useRef(null);

  useEffect(() => {
    loadHistory();
    loadInsights();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const res = await sentinelApi.history();
      setMessages(res.data.messages || []);
      setSessionId(res.data.session_id || "");
    } catch (e) { console.error(e); }
  };

  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const res = await sentinelApi.insights();
      setInsights(res.data.insights || []);
    } catch (e) { console.error(e); }
    setInsightsLoading(false);
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    const userMsg = { role: "user", content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await sentinelApi.chat(text, sessionId);
      const aiMsg = { role: "assistant", content: res.data.response, created_at: new Date().toISOString() };
      setMessages(prev => [...prev, aiMsg]);
      if (res.data.session_id) setSessionId(res.data.session_id);
    } catch (e) {
      const errMsg = { role: "assistant", content: "I'm unable to process your request at the moment. Please try again.", created_at: new Date().toISOString() };
      setMessages(prev => [...prev, errMsg]);
    }
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <div className="h-[calc(100vh-3.5rem-3rem)] flex gap-4" data-testid="sentinel-page">
      {/* Main Chat Panel */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4 shrink-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 flex items-center justify-center">
            <Eye className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="font-barlow font-bold text-xl text-slate-50">The Sentinel</h1>
            <p className="text-[11px] text-slate-500 font-manrope">Your AI financial guardian — observing, advising, never interfering</p>
          </div>
          <div className="ml-auto">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/20 bg-emerald-500/5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[9px] text-emerald-400 font-mono uppercase tracking-wider">Observing</span>
            </div>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto rounded-xl border border-slate-800/40 bg-slate-900/20 p-4 space-y-4 mb-3" data-testid="sentinel-chat-area">
          {messages.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Eye className="w-12 h-12 text-slate-700 mb-4" />
              <h3 className="font-barlow font-semibold text-lg text-slate-400 mb-2">The Sentinel is watching</h3>
              <p className="text-sm text-slate-600 max-w-sm font-manrope mb-6">
                I observe your financial state in real-time. Ask me anything about your cashflow, obligations, or how to improve your financial governance.
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {SUGGESTED_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    onClick={() => sendMessage(q)}
                    className="text-xs text-slate-400 hover:text-cyan-400 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 hover:border-cyan-500/30 rounded-full px-3 py-1.5 transition-all"
                    data-testid={`suggested-q-${q.slice(0, 20).replace(/\s/g, '-')}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] ${msg.role === "user"
                ? "bg-cyan-500/10 border border-cyan-500/20 rounded-2xl rounded-br-md"
                : "bg-slate-800/40 border border-slate-700/30 rounded-2xl rounded-bl-md"
              } px-4 py-3`}>
                {msg.role === "assistant" && (
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Eye className="w-3 h-3 text-cyan-400" />
                    <span className="text-[9px] text-cyan-400 font-mono uppercase tracking-wider">Sentinel</span>
                  </div>
                )}
                <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap font-manrope">{msg.content}</p>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-800/40 border border-slate-700/30 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
                  <span className="text-xs text-slate-500 font-manrope">Sentinel is analysing...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="flex gap-2 shrink-0">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask the Sentinel about your finances..."
            className="bg-slate-900/50 border-slate-700 text-slate-100 h-11 flex-1"
            disabled={loading}
            data-testid="sentinel-chat-input"
          />
          <Button type="submit" disabled={loading || !input.trim()} className="bg-cyan-600 hover:bg-cyan-500 h-11 px-4" data-testid="sentinel-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>

      {/* Right Panel — Proactive Insights */}
      <div className="w-[300px] hidden lg:flex flex-col shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-slate-400 uppercase tracking-wider font-manrope">Live Insights</span>
          </div>
          <button onClick={loadInsights} className="text-slate-600 hover:text-slate-400 transition-colors" data-testid="refresh-insights-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${insightsLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="space-y-3 flex-1 overflow-y-auto">
          {insightsLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-5 h-5 text-slate-600 animate-spin" />
            </div>
          ) : (
            insights.map((insight, i) => {
              const Icon = INSIGHT_ICONS[insight.type] || AlertTriangle;
              const colorClass = INSIGHT_COLORS[insight.type] || INSIGHT_COLORS.warning;
              return (
                <Card key={i} className={`border ${colorClass} bg-slate-900/30`} data-testid={`insight-card-${i}`}>
                  <CardContent className="p-3">
                    <div className="flex items-start gap-2">
                      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                      <div>
                        <span className="text-xs font-semibold block">{insight.title}</span>
                        <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed font-manrope">{insight.message}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Suggested Questions */}
        <div className="mt-3 pt-3 border-t border-slate-800/40">
          <span className="text-[9px] text-slate-600 uppercase tracking-wider font-manrope mb-2 block">Quick Questions</span>
          <div className="space-y-1.5">
            {SUGGESTED_QUESTIONS.slice(0, 3).map((q) => (
              <button
                key={q}
                onClick={() => sendMessage(q)}
                className="w-full text-left text-[11px] text-slate-500 hover:text-cyan-400 bg-slate-800/30 hover:bg-slate-800/60 border border-slate-700/30 hover:border-cyan-500/20 rounded-lg px-3 py-2 transition-all font-manrope"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
